/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import React, {
    useContext, useEffect, useRef, useState, createRef
} from 'react';
import throttle from 'lodash.throttle';
import { ExpansionPanelGroup } from '@ferris/react/components/expansion-panel';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { Tabs, TabItem } from '@ferris/react/components/tabs';
import { shallowEqual, useSelector } from 'react-redux';
import TranslationUtil from '../../utils/TranslationUtil';
import { createImpressionEvent, createInteractionEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import CustomExpansionPanel from './CustomExpansionPanel';
import log from '../../utils/ClientLogger';

const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) func();
        else didMount.current = true;
    }, deps);
};

export const tabArrowNavigation = tabsRef => (e) => {
    try {
        const focusTab = (index) => {
            if (index >= 0 && index < tabsRef.current.length) {
                tabsRef.current[index].focus();
            } else if (index < 0) {
                tabsRef.current[tabsRef.current.length - 1].focus();
            } else if (index >= tabsRef.current.length) {
                tabsRef.current[0].focus();
            }
        };

        const currentFocused = tabsRef.current.findIndex(t => t === document.activeElement);
        if (currentFocused !== -1) {
            switch (e.key) {
            case 'ArrowLeft':
                focusTab(currentFocused - 1);
                break;
            case 'ArrowRight':
                focusTab(currentFocused + 1);
                break;
            default:
                break;
            }
        }
    } catch (error) {
        log('error', 'tabArrowNavigation', { error });
    }
};

export const tabUseEffect = (tabSection, tabsRef) => () => {
    let tabListNavFn;
    if (tabSection.current && tabSection.current?.tabListRefContainer.current) {
        const tabList = tabSection.current.tabListRefContainer.current;
        const tabs = tabList.querySelectorAll('a');
        tabs.forEach((t, i) => {
            // eslint-disable-next-line no-param-reassign
            tabsRef.current[i] = t;
            if (i > 0) {
                t.setAttribute('tabindex', '-1');
            }
        });
        tabListNavFn = tabArrowNavigation(tabsRef);
        (tabList).addEventListener('keydown', tabListNavFn);
    }
    return () => {
        if (tabSection.current && tabSection.current?.tabListRefContainer.current) {
            const tabList = tabSection.current.tabListRefContainer.current;
            tabList.removeEventListener('keydown', tabListNavFn);
        }
    };
};
const FAQs = () => {
    const getPropsFromState = (state) => {
        const { app } = state;
        const { features } = app;
        const { isFrboFlow } = features;
        const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
        const prefix = isFrboFlow ? 'Vrbo' : 'EG';
        const getTranslation = suffix => translations.getTranslation(`${prefix}.FAQs.${suffix}`,
            {
                siteName: app.displayBrand,
                brand: app.displayBrand,
                brandName: app.displayBrand
            });
        const vrboFaqs = [
            {
                title: getTranslation('subtitle1'),
                qaPairs: [
                    { question: getTranslation('question1'), answer: getTranslation('answer1') },
                    { question: getTranslation('question2'), answer: getTranslation('answer2') },
                    { question: getTranslation('question3'), answer: getTranslation('answer3') },
                    { question: getTranslation('question12'), answer: getTranslation('answer12') }
                ]
            },
            {
                title: getTranslation('subtitle2'),
                qaPairs: [
                    { question: getTranslation('question4'), answer: getTranslation('answer4') },
                    { question: getTranslation('question5'), answer: getTranslation('answer5') },
                    { question: getTranslation('question6'), answer: getTranslation('answer6') },
                    { question: getTranslation('question7'), answer: getTranslation('answer7') }
                ]
            },
            {
                title: getTranslation('subtitle3'),
                qaPairs: [
                    { question: getTranslation('question8'), answer: getTranslation('answer8') },
                    { question: getTranslation('question9'), answer: getTranslation('answer9') },
                    { question: getTranslation('question10'), answer: getTranslation('answer10') },
                    { question: getTranslation('question11'), answer: getTranslation('answer11') }
                ]
            }
        ];
        const egFaqs = [
            {
                title: getTranslation('subtitle1'),
                qaPairs: [
                    { question: getTranslation('question1'), answer: getTranslation('answer1') },
                    { question: getTranslation('question2'), answer: getTranslation('answer2') },
                    { question: getTranslation('question3'), answer: getTranslation('answer3') },
                    { question: getTranslation('question4'), answer: getTranslation('answer4') }
                ]
            },
            {
                title: getTranslation('subtitle2'),
                qaPairs: [
                    { question: getTranslation('question5'), answer: getTranslation('answer5') },
                    { question: getTranslation('question6'), answer: getTranslation('answer6') },
                    { question: getTranslation('question7'), answer: getTranslation('answer7') }
                ]
            },
            {
                title: getTranslation('subtitle3'),
                qaPairs: [
                    { question: getTranslation('question8'), answer: getTranslation('answer8') },
                    { question: getTranslation('question9'), answer: getTranslation('answer9') },
                    { question: getTranslation('question10'), answer: getTranslation('answer10') }
                ]
            }
        ];

        return {
            faqs: isFrboFlow ? vrboFaqs : egFaqs
        };
    };

    const { faqs } = useSelector(getPropsFromState, shallowEqual);

    const { trigger } = useContext(TrackingContext);

    const faqSectionRef = useRef();

    const isFaqSectionVisible = () => {
        /* istanbul ignore else */
        if (faqSectionRef.current) { // protect against SSR.
            // eslint-disable-next-line max-len
            return (faqSectionRef.current.getBoundingClientRect().top - (faqSectionRef.current.offsetHeight + 150)) < 0;
        }
        return false;
    };

    const [faqSeen, setFaqSeen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (faqSeen) {
            trigger('impression', createImpressionEvent('faq_section.viewed', 'Property Onboarding', '', true, '2.3.0'));
        }
    }, [faqSeen]);

    const onScroll = () => {
        if (isFaqSectionVisible()) {
            setFaqSeen(true);
        }
    };
    const scrollHandler = throttle(onScroll, 333);

    const tabSection = useRef();
    const tabsRef = useRef([]);
    const firstPanelRefs = useRef([]);

    useEffect(() => {
        if (faqs.length > 0) {
            firstPanelRefs.current = Array(faqs.length).fill(null).map(
                (_, index) => firstPanelRefs.current[index] || createRef()
            );
        }
    }, [faqs]);

    useDidMountEffect(() => {
        requestAnimationFrame(() => {
            const activePanel = firstPanelRefs.current[activeTab]?.current;
            if (activePanel) {
                const focusableElement = activePanel.querySelector('.expansion-panel');
                if (focusableElement) {
                    focusableElement.focus({ preventScroll: true });
                }
            }
        });
    }, [activeTab]);

    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        trigger('impression', createImpressionEvent('faq_section.presented', 'Property Onboarding', '', true, '2.3.0'));
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, []);


    useEffect(tabUseEffect(tabSection, tabsRef), []);

    const clickTrigger = identifier => (opened) => {
        trigger('interaction', createInteractionEvent('faq_section.selected', 'Property Onboarding', `${identifier}.${opened ? 'open' : 'close'}`, undefined, true, '2.3.0'));
    };


    return (
        <section id="faqs-section" className="faqs-section" itemScope itemType="https://schema.org/FAQPage" ref={faqSectionRef} aria-label="Frequently Asked Questions">
            <h2 className="title">FAQs</h2>
            <Tabs
                onTabItemClick={(selected, index) => {
                    setActiveTab(index);
                }}
                scrollable={false}
                classes="tabs"
                ref={tabSection}
                role="tablist"
                aria-label="FAQ Categories"
            >
                {faqs.map((subTitle, tabIndex) => (
                    <TabItem
                        key={`tab-${tabIndex + 1}`}
                        classes="tab-item"
                        contentId={`tab-content-${tabIndex + 1}`}
                        selected={tabIndex === activeTab}
                        tabTitle={<p className="tab-title">{subTitle.title}</p>}
                        aria-controls={`tab-content-${tabIndex + 1}`}
                    >
                        <ExpansionPanelGroup
                            offset
                            classes="expansion-panel-group"
                            itemScope
                            itemProp="mainEntity"
                            itemType="https://schema.org/Question"
                            aria-label={`${subTitle.title} Questions and Answers`}
                        >
                            {subTitle.qaPairs.map((qaPair, i) => (
                                <CustomExpansionPanel
                                    key={`panel-${tabIndex}-${i}`}
                                    question={qaPair.question}
                                    answer={qaPair.answer}
                                    onToggle={clickTrigger(`${subTitle.title}.${i}`)}
                                    ref={i === 0 ? firstPanelRefs.current[tabIndex] : null}
                                />
                            ))}
                        </ExpansionPanelGroup>
                    </TabItem>
                ))}
            </Tabs>
        </section>
    );
};

FAQs.displayName = 'FAQs';
export default FAQs;
